import { z } from 'zod';

import type { FormAnswerValue, FormField } from '@/types';

/**
 * Builds a Zod schema from a runtime list of `FormField` definitions. Used by
 * `DynamicFormRenderer` consumers to wire up validation for user-authored
 * forms (onboarding, event registration, lead magnets, etc).
 *
 * Rules:
 *   - `NUMBER`   → coerced `number`; required means `>= 1`.
 *   - `MULTI_SELECT` → `string[]`; required means at least one item.
 *   - `DATE` / `IMAGE` → `string`; required means non-empty.
 *   - Everything else (`TEXT`, `TEXTAREA`, `SELECT`) → `string`.
 *
 * Optional fields are `optional()`. `DATE` and `IMAGE` are additionally
 * `nullable()` because those renderers emit `null` when cleared.
 */
export function buildDynamicZodSchema(fields: FormField[] | null | undefined) {
  if (!fields || fields.length === 0) return z.object({});

  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    let schema: z.ZodTypeAny;

    switch (field.type) {
      case 'NUMBER':
        schema = z.coerce.number();
        schema = field.required
          ? (schema as z.ZodNumber).min(1, `${field.label} is required`)
          : schema.optional();
        break;
      case 'MULTI_SELECT':
        schema = z.array(z.string());
        schema = field.required
          ? (schema as z.ZodArray<z.ZodString>).min(
              1,
              `Please select at least one option for ${field.label}`
            )
          : schema.optional();
        break;
      case 'DATE':
        schema = z.string();
        schema = field.required
          ? (schema as z.ZodString).min(1, `${field.label} is required`)
          : schema.optional().nullable();
        break;
      case 'IMAGE':
        schema = z.string();
        schema = field.required
          ? (schema as z.ZodString)
              .url(`Please upload an image for ${field.label}`)
              .min(1, `${field.label} is required`)
          : schema.optional().nullable();
        break;
      default:
        schema = z.string();
        schema = field.required
          ? (schema as z.ZodString).min(1, `${field.label} is required`)
          : schema.optional();
    }

    shape[field.id] = schema;
  }

  return z.object(shape);
}

/** Detect whether a field's label hints at a phone / contact number. */
export function isPhoneField(field: FormField): boolean {
  if (field.type !== 'TEXT') return false;
  const l = field.label.toLowerCase();
  return l.includes('phone') || l.includes('contact') || l.includes('whatsapp');
}

/**
 * Returns the appropriate "empty" default for a given field type. Use when
 * seeding `react-hook-form` `defaultValues` to avoid controlled-to-uncontrolled
 * warnings for fields that the user hasn't answered yet.
 */
export function getDefaultValueForField(field: FormField): FormAnswerValue {
  switch (field.type) {
    case 'MULTI_SELECT':
      return [];
    case 'DATE':
    case 'IMAGE':
      return null;
    case 'NUMBER':
      // RHF + coerce.number() happily accepts empty string; avoids
      // controlled→uncontrolled warnings on the underlying <input>.
      return '';
    default:
      return '';
  }
}

/**
 * Merges user-supplied initial answers with type-appropriate defaults for any
 * fields that are missing. Ensures every field has a controlled value from
 * first render.
 */
export function buildInitialAnswers(
  fields: FormField[],
  provided: Record<string, FormAnswerValue> = {}
): Record<string, FormAnswerValue> {
  const merged: Record<string, FormAnswerValue> = {};
  for (const field of fields) {
    const existing = provided[field.id];
    merged[field.id] = existing === undefined ? getDefaultValueForField(field) : existing;
  }
  return merged;
}
